import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { ABG } from './abg';
import API_BASE_URL from '../apiConfig';
import './DominoGame.css';
import audioManager from './AudioManager'; 
import { commonIcons, dominoAssets, getFlag } from './assetImports';
import { useTranslation } from 'react-i18next';

const DominoGame = ({ onRematch }) => {
    const { t } = useTranslation('games');
    const { socket, user, updateUserDetails } = useContext(AuthContext);

    // State variables
    const [game, setGame] = useState(null);
    const navigate = useNavigate();
    const [opponents, setOpponents] = useState([]);
    const [isGameEnd, setIsGameEnd] = useState(false);
    const [playerTiles, setPlayerTiles] = useState([]);
    const [isMoveLoading, setIsMoveLoading] = useState(false);
    const [opponentTiles, setOpponentTiles] = useState([]);
    const [showActionButtons, setShowActionButtons] = useState(false);
    const [currentRound, setCurrentRound] = useState(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [roundResults, setRoundResults] = useState([]);
    const [animateRound, setAnimateRound] = useState(false);
    const [ShowCancelPopup, setShowCancelPopup] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [isWaitingForRematch, setIsWaitingForRematch] = useState(false);
    const [isSoundOn, setIsSoundOn] = useState(true); 
    const DEFAULT_VOLUME = 0.8;
    audioManager.setSoundOn(isSoundOn);
    
    const [dominoShowTextMenu, setDominoShowTextMenu] = useState(false);
    const [dominoShowSettingsMenu, setDominoShowSettingsMenu] = useState(false);
    const [dominoPlayerMessage, setDominoPlayerMessage] = useState(null);
    const [dominoOpponentMessage, setDominoOpponentMessage] = useState(null);
    const [isLeaveGamePopupVisible, setIsLeaveGamePopupVisible] = useState(false);
    const [boardTiles, setBoardTiles] = useState([]);
    const [currentPlayerTurn, setCurrentPlayerTurn] = useState(null);
    const [gameScore, setGameScore] = useState({ player: 0, opponent: 0 });

    function playDominoSound(soundName) {
        audioManager.setVolume(DEFAULT_VOLUME);
        audioManager.playSound(soundName);
    }

    useEffect(() => {
        if (currentRound !== null && currentRound > 1) {
            setAnimateRound(true);
            const timer = setTimeout(() => setAnimateRound(false), 2400); 
            return () => clearTimeout(timer); 
        }
    }, [currentRound]);

    useEffect(() => {
        socket.on('game-replay', onRematch);
        return () => socket.off('game-replay', onRematch);
    }, [onRematch]);

    useEffect(() => {
        const handleTouchStart = (event) => {
            event.preventDefault(); 
        };
    
        document.addEventListener("pointerdown", handleTouchStart, { passive: false });
        document.addEventListener("touchstart", handleTouchStart, { passive: false });
    
        return () => {
            document.removeEventListener("pointerdown", handleTouchStart);
            document.removeEventListener("touchstart", handleTouchStart);
        };
    }, []);

    useEffect(() => {
        let timer;
        if (countdown > 0 && playerTiles.length > 0 && currentPlayerTurn === user._id) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [countdown, playerTiles, currentPlayerTurn, user._id]);

    const fetchGameState = useCallback(async () => {
        try {
            const response = await apiRequest('/api/current-game', {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();

            if (response.ok && data.game) {
                if (data.game.status === 'completed') {
                    navigate('/game');
                    return; 
                }
            
                setGame(data.game);
                setCurrentRound(data.game.currentRound);
                setOpponents(data.opponents || []);
                handleGamePhase(data.game);
            } else {
                setGame(null);
                setPopupMessage('Failed to fetch the current game state.');
                navigate('/game');
                return;
            }
        } catch (error) {
            console.error('Error fetching game state:', error);
            setGame(null);
            navigate('/game');
            return;
        }
    }, [navigate]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        socket.on('connect', () => {
            fetchGameState();
        });
        
        socket.on('game-update', (data) => {
            if (!data.game) {
                navigate('/game');
                return; 
            }

            setGame(data.game);
            setCurrentRound(data.game.currentRound);
            setOpponents(data.opponents || []);
            handleGamePhase(data.game);
        });

        socket.on('domino-message', ({ message, userID }) => {
            if(user._id !== userID) {
                setDominoOpponentMessage(t(`quickChat.${message}`));
                setTimeout(() => setDominoOpponentMessage(null), 2500);
            }
        });

        return () => {
            socket.off('game-update');
            socket.off('connect');
            socket.off('domino-message');
        };
    }, [socket, user, navigate]);

    const handleGamePhase = (gameData) => {
        switch (gameData.phase) {
            case 'deal':
                setPlayerTiles(gameData.playerTiles || []);
                setBoardTiles(gameData.boardTiles || []);
                setCurrentPlayerTurn(gameData.currentPlayerTurn || null);
                setGameScore(gameData.gameScore || { player: 0, opponent: 0 });
                break;

            case 'player-turn':
                setShowActionButtons(true);
                setCountdown(30);
                setCurrentPlayerTurn(gameData.currentPlayerTurn);
                break;

            case 'round-completed':
                setShowActionButtons(false);
                setBoardTiles(gameData.boardTiles || []);
                setGameScore(gameData.gameScore || { player: 0, opponent: 0 });
                setCountdown(30);
                break;

            case 'game-canceled':
                setPlayerTiles([]);
                setBoardTiles([]);
                setShowCancelPopup(true); 
                setTimeout(() => {
                    setShowCancelPopup(false);
                    navigate('/game'); 
                }, 4000);
                break;

            case 'game-completed':
                setIsGameEnd(true);
                break;
                
            default:
                console.warn(`Unhandled game phase: ${gameData.phase}`);
        }
    };

    const handlePlayerMove = async (tileId, placement, side = null) => {
        playDominoSound('buttonClick');
        setIsMoveLoading(true);
    
        try {
            const response = await apiRequest('/api/domino-move', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameId: game._id,
                    tileId: tileId,
                    placement: placement,
                    side: side
                }),
            });
    
            if (!response.ok) throw new Error('Failed to make a move.');
            setShowActionButtons(false);
        } catch (error) {
            console.error('Error sending move:', error);
        } finally {
            setIsMoveLoading(false);
        }
    };

    const handleDrawTile = async () => {
        playDominoSound('buttonClick');
        setIsMoveLoading(true);
    
        try {
            const response = await apiRequest('/api/domino-draw', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameId: game._id
                }),
            });
    
            if (!response.ok) throw new Error('Failed to draw a tile.');
        } catch (error) {
            console.error('Error drawing tile:', error);
        } finally {
            setIsMoveLoading(false);
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchGameState();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchGameState]); 

    useEffect(() => {
        fetchGameState();
    }, [fetchGameState]);

    const triggerConfettiAnimation = () => {
        const confettiContainer = document.createElement('div');
        confettiContainer.className = 'confetti-container';

        for (let i = 0; i < 200; i++) { 
            const confetti = document.createElement('div');
            confetti.className = 'confetti-piece';
            
            confetti.style.left = '50%';
            confetti.style.top = '50%'; 
            confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`; 
            confetti.style.transform = `rotate(${Math.random() * 360}deg)`; 

            const randomX = (Math.random() - 0.5) * window.innerWidth * 2; 
            const randomY = (Math.random() - 0.5) * window.innerHeight * 2;
            const duration = Math.random() * 1.4 + 0.8; 

            confetti.style.animation = `explode ${duration}s ease-out forwards`;
            confetti.style.setProperty('--randomX', `${randomX}px`); 
            confetti.style.setProperty('--randomY', `${randomY}px`); 

            confettiContainer.appendChild(confetti);
        }

        document.body.appendChild(confettiContainer);

        setTimeout(() => {
            document.body.removeChild(confettiContainer);
        }, 4000);  
    };

    useEffect(() => {
        if (game && user && isGameEnd) {
            if (game.winners?.includes(user._id)) {
                triggerConfettiAnimation();
                playDominoSound('win'); 
            } else {
                playDominoSound('lose'); 
            }
        }
    }, [isGameEnd, game, user._id, navigate]);
    
    const [showBonusDetails, setShowBonusDetails] = useState(false);
    
    useEffect(() => {
        if (isGameEnd) {
            updateUserDetails();
        }
    }, [isGameEnd]);
    
    const renderGameEndPopup = () => {
        if (!isGameEnd || !game || !game.players) return null;

        const currentPlayer = game.players.find(player => 
            player.userId.toString() === user._id.toString()
        );
        
        if (!currentPlayer) return null;

        const winnersArray = game.winners || [];
        const winnerId = winnersArray[0] || null;
        const isUserWinner = winnerId === user._id;
        const currency = currentPlayer.currency;
        const betAmount = currentPlayer.betAmount;
        const winnings = isUserWinner ? calculateWinnerGain(betAmount, currency) : 0;
        const endReason = game.endReason || null;

        const gamesPlayedToday = user.dailyGameStats?.[`${currency}GamesPlayed`] || 0;
        const gamesRemaining = Math.max(0, 10 - gamesPlayedToday);
        const showBonusOffer = gamesRemaining > 0;

        const getCurrencyIcon = () => {
            switch(currency) {
                case 'token':
                    return <div className={`domino-${isUserWinner ? 'Win' : 'Lose'}TokenCoin-icon`}></div>;
                case 'stars':
                    return <div className="domino-star-icon"></div>;
                case 'usdt':
                    return <div className="domino-dollar-sign-icon">$</div>;
                default:
                    return null;
            }
        };

        const getCurrencyBonusIcon = () => {
            switch(currency) {
                case 'token':
                    return <div className="domino-tokenYellow-icon"></div>;
                case 'stars':
                    return <div className="domino-star-icon"></div>;
                case 'usdt':
                    return <div className="domino-dollar-sign-icon">$</div>;
                default:
                    return null;
            }
        };

        const getBonusReward = () => {
            switch(currency) {
                case 'token': return 100;
                case 'stars': return 500;
                case 'usdt': return 10;
                default: return 0;
            }
        };

        const handleGetBonus = () => {
            setShowBonusDetails(true);
        };

        return (
            <>
                <div className="domino-popup-overlay"></div>
                <div className="domino-end-game-popup">
                    {isUserWinner && !isWaitingForRematch && <div className="domino-win-background-animated"></div>}
                    <div className="domino-screen-blur"></div>
                    <div className="domino-popup-background"></div>

                    {!isWaitingForRematch && (
                        <div className="domino-popup-content">
                            {!showBonusDetails ? (
                                <>
                                    <h2 className="domino-popup-title">
                                        {isUserWinner 
                                            ? (!endReason ? t('popup.youWon') : t('popup.opponentLeft'))
                                            : t('popup.youLose')}
                                    </h2>

                                    <div className="domino-points-change">
                                        {isUserWinner ? (
                                            <>
                                                <span className="domino-points-positive">
                                                    <span className="domino-sign">+</span>
                                                    <span className="domino-number">{winnings}</span>
                                                </span>
                                                {getCurrencyIcon()}
                                            </>
                                        ) : (
                                            <>
                                                <span className="domino-points-negative">
                                                    <span className="domino-sign">-</span>
                                                    <span className="domino-number">{betAmount}</span>
                                                </span>
                                                {getCurrencyIcon()}
                                            </>
                                        )}
                                    </div>

                                    <div className="domino-total-score">
                                        {t('labels.totalScore')}: <span>{user.score}</span>
                                    </div>

                                    <div className="domino-popup-buttons">
                                        <button 
                                            className="domino-exit-btn" 
                                            onPointerUp={(e) => handleExit(e)}
                                        />
                                        
                                        {showBonusOffer && (
                                            <button 
                                                className="domino-get-bonus-btn" 
                                                onPointerUp={handleGetBonus}
                                            >
                                                {t('buttons.getBonus')}
                                            </button>
                                        )}

                                        {!showBonusOffer && (
                                            <button 
                                                className="domino-replay-btn" 
                                                onPointerUp={(e) => handleReplay(e, game, user._id)}
                                            >
                                                {t('buttons.replay')}
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="domino-popup-title-small">
                                        {t('popup.playMoreGames', { count: gamesRemaining })}
                                    </h2>

                                    <div className="domino-points-change-bonus">
                                        <span className="domino-points-bonus">
                                            <span className="domino-sign">+</span>
                                            <span className="domino-number">{getBonusReward()}</span>
                                        </span>
                                        {getCurrencyBonusIcon()}
                                    </div>

                                    <div className="domino-popup-buttons">
                                        <button 
                                            className="domino-exit-btn" 
                                            onPointerUp={(e) => handleExit(e)}
                                        />
                                        
                                        <button 
                                            className="domino-play-btn" 
                                            onPointerUp={(e) => handleReplay(e, game, user._id)}
                                        >
                                            {t('buttons.play')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isWaitingForRematch && (
                        <div className="domino-popup-loading">
                            <div className="domino-loading-spinner"></div>
                            <div className="domino-waiting-text">{t('popup.waitingForOpponent')}</div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const calculateWinnerGain = (betAmount, currency) => {
        const payoutMap = {
            token: {
                25: 25
            },
            stars: {
                300: 200,
                1200: 800,
                6000: 4000
            },
            usdt: {
                3: 2,  
                12: 8,
                60: 40
            }
        };
        
        if (!payoutMap[currency]) {
            console.error(`Unsupported currency: ${currency}`);
            return 0;
        }

        const gain = payoutMap[currency][betAmount];
        if (gain === undefined) {
            console.error(`Invalid bet amount: ${betAmount} for currency: ${currency}`);
            return 0;
        }

        return gain;
    };

    const handleExit = (e) => {
        e.preventDefault(); 
        navigate('/game'); 
    };

    const handleReplay = async (e, currentGame, userid) => {
        e.preventDefault(); 
        if (!currentGame || !socket.id || !currentGame.players) return;
        
        const currentPlayer = currentGame.players.find(player => 
            player.userId.toString() === userid.toString() 
        );
        
        if (!currentPlayer) return;

        const gameMode = currentGame.gameMode;
        const socketId = socket.id;
        const amount = currentPlayer.betAmount;
        const currency = currentPlayer.currency;
        const gameId = currentGame._id;

        try {
            const response = await apiRequest(`${API_BASE_URL}/api/start-game`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    betAmount: amount, 
                    gameMode, 
                    socketId, 
                    isRematch: true,
                    currency,
                    isPrived: !!currentGame.isPrivate, 
                    referrerId: currentGame.userIds?.[0] || null  
                })
            });
            const data = await response.json();
        
            if (response.ok) {
                setIsWaitingForRematch(true);
            } else {
                console.log("FAILED", data?.message);
            }
        } catch (err) {
            console.error("Rematch error:", err);
        }
    };

    const renderGameScore = () => {
        if (!game) return null;
        
        const playerScore = gameScore.player || 0;
        const opponent = opponents.find(o => o._id !== user._id);
        const opponentScore = gameScore.opponent || 0;
        
        if (playerScore === 0 && opponentScore === 0) return null;
    
        return (
            <div className={`domino-score-display ${animateRound ? 'domino-animate' : ''}`}>
                <div className="domino-score-box">{playerScore}</div>
                <div className="domino-score-separator">:</div>
                <div className="domino-score-box">{opponentScore}</div>
            </div>
        );
    };

    const renderAvatar = (name, photoUrl, customAvatar) => {
        if (customAvatar) {
            return <img src={customAvatar} alt={`${name}'s Custom Avatar`} className="domino-avatar-image" />;
        }
        if (photoUrl) {
            return <img src={photoUrl} alt={`${name}'s Avatar`} className="domino-avatar-image" />;
        }
        const initial = name.charAt(0).toUpperCase(); 
        const avatarStyle = ABG(name);
        return <div className="domino-avatar-placeholder" style={avatarStyle}>{initial}</div>;
    };

    const renderOpponentArea = (game, user, opponents) => {
        const opponent = opponents.find(opponent => opponent._id !== user._id);
      
        if (!opponent) return null;
      
        const opponentWins = game.totalGamePoints[opponent._id] || 0;
      
        return (
            <div className="domino-opponent-wrapper">
                <div className="domino-opponent-area">
                    <div className="domino-opponent-avatar">
                        <div className="domino-avatar-gradient-border"></div>
                        {renderAvatar(opponent.nickname, opponent.photoUrl, opponent.customAvatar)}
                    </div>
                    {renderDominoOpponentMessage()}

                    <div className="domino-opponent-info">
                        <div className="domino-name-and-flag">
                            <span className="domino-opponent-name">{opponent.nickname}</span>
                            {opponent.country && (
                                <img
                                    src={getFlag(opponent.country)}
                                    alt={`${opponent.country} flag`}
                                    className="domino-country-flag"
                                />
                            )}
                        </div>
                        <span className="domino-opponent-score"> {t('labels.score')}: {opponent.score}</span>
                    </div>
        
                    <div className="domino-opponent-trophies">
                        {Array(3)
                            .fill(0)
                            .map((_, index) => (
                                <div key={index} className="domino-trophy-slot">
                                    {index < opponentWins && (
                                        <div className="domino-trophy-glow-ellipse"></div>
                                    )}
                                    <div
                                        className="domino-player-trophy-icon"
                                        style={{
                                            backgroundImage: `url(${index < opponentWins ? commonIcons.trpOn : commonIcons.trp})`,
                                        }}
                                    ></div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderDominoPlayerArea = (game, user) => {
        if (!user) return null;
    
        const playerWins = game.totalGamePoints[user._id] || 0;
    
        return (
            <div className="domino-player-wrapper">
                <div className="domino-player-area">
                    {renderDominoPlayerMessage()}
                    <div className="domino-player-avatar">
                        <div className="domino-avatar-gradient-border"></div>
                        {renderAvatar(user.nickname, user.photoUrl, user.customAvatar)}
                    </div>
    
                    <div className="domino-player-info">
                        <div className="domino-name-and-flag">
                            <span className="domino-player-name">{user.nickname}</span>
                            {user.country && (
                                <img
                                    src={getFlag(user.country)}
                                    alt="flag"
                                    className="domino-country-flag"
                                />
                            )}
                        </div>
                        <span className="domino-player-score">{t('labels.score')}: {user.score}</span>
                    </div>
    
                    <div className="domino-player-trophies">
                        {Array(3)
                            .fill(0)
                            .map((_, index) => (
                                <div key={index} className="domino-trophy-slot">
                                    {index < playerWins && (
                                        <div className="domino-trophy-glow-ellipse"></div>
                                    )}
                                    <div
                                        className="domino-player-trophy-icon"
                                        style={{
                                            backgroundImage: `url(${index < playerWins ? commonIcons.trpOn : commonIcons.trp})`
                                        }}
                                    ></div>
                                </div>
                            ))}
                    </div>
                    {renderDominoControlButtons()}
                </div>
            </div>
        );
    };

    const renderPopup = () => {
        if (ShowCancelPopup) {
            return (
                <div className="popup">
                    <div className="popup-inner">
                        <h3>{t('popup.gameCanceled')}</h3>
                        <p>{t('popup.cancelReason')}</p>
                        <button onClick={() => setShowCancelPopup(false)}>
                            {t('buttons.close')}
                        </button>
                    </div>
                </div>
            );
        }
    };

    const renderPlayerTiles = () => {
        if (!playerTiles.length) return null;
        
        return (
            <div className="domino-player-tiles">
                {playerTiles.map((tile, index) => (
                    <div 
                        key={index} 
                        className="domino-tile"
                        onPointerUp={() => handleTileClick(tile)}
                    >
                        <div className="domino-tile-top">{tile[0]}</div>
                        <div className="domino-tile-middle"></div>
                        <div className="domino-tile-bottom">{tile[1]}</div>
                    </div>
                ))}
            </div>
        );
    };

    const handleTileClick = (tile) => {
        if (currentPlayerTurn !== user._id || isMoveLoading) return;
        
        // Check if this is the first move
        if (boardTiles.length === 0) {
            handlePlayerMove(tile, 'board');
            return;
        }
        
        // For subsequent moves, we would show placement options
        // This is a simplified version - in a real game you'd need UI for placement
        const leftValue = boardTiles[0][0];
        const rightValue = boardTiles[boardTiles.length - 1][1];
        
        if (tile[0] === leftValue || tile[1] === leftValue) {
            handlePlayerMove(tile, 'left', tile[0] === leftValue ? 'normal' : 'flipped');
        } else if (tile[0] === rightValue || tile[1] === rightValue) {
            handlePlayerMove(tile, 'right', tile[0] === rightValue ? 'normal' : 'flipped');
        } else {
            // Tile doesn't match either end
            setPopupMessage(t('domino.invalidMove'));
            setTimeout(() => setPopupMessage(''), 2000);
        }
    };

    const renderBoard = () => {
        if (!boardTiles.length) return null;
        
        return (
            <div className="domino-board">
                {boardTiles.map((tile, index) => (
                    <div key={index} className="domino-board-tile">
                        <div className="domino-tile-top">{tile[0]}</div>
                        <div className="domino-tile-middle"></div>
                        <div className="domino-tile-bottom">{tile[1]}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderActionButtons = () => {
        if (!showActionButtons || currentPlayerTurn !== user._id) return null;
        
        return (
            <div className="domino-action-buttons">
                <div className="domino-timer-wrapper">
                    {countdown > 0 ? (
                        <>
                            {countdown.toString().padStart(2, '0').split('').map((digit, index) => (
                                <div key={index} className="domino-timer-box">
                                    {digit}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className="domino-auto-action-message">{t('domino.autoPassing')}</div>
                    )}
                </div>
                
                <button 
                    className="domino-action-btn domino-draw-btn"
                    onPointerUp={handleDrawTile}
                    disabled={isMoveLoading}
                >
                    {t('domino.draw')}
                </button>
                
                <button 
                    className="domino-action-btn domino-pass-btn"
                    onPointerUp={() => handlePlayerMove(null, 'pass')}
                    disabled={isMoveLoading}
                >
                    {t('domino.pass')}
                </button>
            </div>
        );
    };

    const renderDominoControlButtons = () => {
        if (!game) return null;
      
        return (
            <div className="domino-game-buttonsIcons">
                <button
                    className={`domino-settingsIcon-buttonIcon ${dominoShowSettingsMenu ? "active" : ""}`}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        setDominoShowSettingsMenu(!dominoShowSettingsMenu);
                    }}
                >
                    <img src={commonIcons.settingsIcon} alt="Settings" />
                </button>
      
                {dominoShowSettingsMenu && (
                    <div className="domino-settings-expanded">
                        <button
                            className="domino-leaveGame-buttonExt"
                            onPointerDown={(e) => {
                                e.stopPropagation();
                                handleLeaveGame(e);
                            }}
                        >
                            <img src={commonIcons.exitIcon} alt="Leave Game" />
                        </button>
      
                        <button
                            className="domino-soundToggle-buttonExt"
                            onPointerDown={(e) => {
                                e.stopPropagation();
                                handleSoundToggle(e);
                            }}
                        >
                            <img src={isSoundOn ? commonIcons.soundIcon : commonIcons.soundOffIcon} alt="Sound" />
                        </button>
                    </div>
                )}
      
                <button
                    className="domino-textIcon-buttonIcon"
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        setDominoShowTextMenu(!dominoShowTextMenu);
                    }}
                >
                    <img src={commonIcons.textIcon} alt="Text" />
                </button>
      
                {dominoShowTextMenu && (
                    <div className="domino-dropdown-menu domino-text-menu">
                        <ul>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('hello'); 
                            }}>
                                {t('quickChat.hello')}
                            </li>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('goodLuck'); 
                            }}>
                                {t('quickChat.goodLuck')}
                            </li>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('wellPlayed'); 
                            }}>
                                {t('quickChat.wellPlayed')}
                            </li>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('thanks'); 
                            }}>
                                {t('quickChat.thanks')}
                            </li>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('oops'); 
                            }}>
                                {t('quickChat.oops')}
                            </li>
                            <li onPointerUp={(e) => { 
                                e.stopPropagation(); 
                                handleDominoMessageSelect('niceMove'); 
                            }}>
                                {t('quickChat.niceMove')}
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        );
    };
      
    const renderDominoPlayerMessage = () => {
        return (
            dominoPlayerMessage && (
                <div className="domino-message-popup domino-player-message">
                    <div className="domino-message-popup-inner domino-player-message-inner">
                        {dominoPlayerMessage}
                    </div>
                </div>
            )
        );
    };
    
    const renderDominoOpponentMessage = () => {
        return (
            dominoOpponentMessage && (
                <div className="domino-message-popup domino-opponent-message">
                    <div className="domino-message-popup-inner domino-opponent-message-inner">
                        {dominoOpponentMessage}
                    </div>
                </div>
            )
        );
    };
    
    const renderLeaveGamePopup = () => {
        if (!isLeaveGamePopupVisible || isGameEnd) return null;
        
        return (
            <>
                <div className="domino-popup-overlay"></div>
                <div className="domino-screen-blur" />
      
                <div className="domino-end-game-popup">
                    <div className="domino-popup-background" />
                    <div className="domino-leave-popup-content">
                        <div className="domino-leave-popup-title">
                            {t('leaveGame.warning')}
                        </div>
      
                        <div className="domino-leave-popup-buttons">
                            <button
                                className="domino-leave-popup-btn"
                                onPointerUp={(e) => handleConfirmLeave(e, game?._id)}
                            >
                                {t('buttons.leave')}
                            </button>
                            <button
                                className="domino-leave-popup-btn"
                                onPointerUp={(e) => handlePopupClose(e)}
                            >
                                {t('buttons.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    };
    
    const handleDominoMessageSelect = (message) => {
        setDominoPlayerMessage(t(`quickChat.${message}`));
        setTimeout(() => setDominoPlayerMessage(null), 2500);
    
        socket.emit('domino-message', { message, gameId: game._id, userID: user._id });
        setDominoShowTextMenu(false);
    };
    
    const handleSoundToggle = (e) => {
        e.stopPropagation();
        setIsSoundOn((prev) => !prev);
    };
    
    const handleLeaveGame = (e) => {
        e.stopPropagation();
        setIsLeaveGamePopupVisible(true);
    };
    
    const handlePopupClose = (e) => {
        e.preventDefault();
        setIsLeaveGamePopupVisible(false);
    };
    
    const handleConfirmLeave = async (e, gameId) => {
        e.preventDefault();
    
        if (!gameId) {
            setIsLeaveGamePopupVisible(false);
            return;
        } 
       
        try {
            const response = await fetch('/api/leave-domino-game', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ gameId }),
            });
    
            const data = await response.json();
    
            if (response.ok) {
                setIsLeaveGamePopupVisible(false);
            } else {
                console.error('Failed to leave game:', data.message);
            }
        } catch (error) {
            console.error('Error leaving game:', error);
        }
    };

    if (!game) {
        return (
            <div className="domino-container">
                <h2>{t('loading.loadingGame')}</h2>
            </div>
        );
    }

    return (
        <div className="domino-container">
            {game && renderOpponentArea(game, user, opponents, currentRound)}
            
            <div className="domino-game-area">
                {renderBoard()}
                {renderGameScore()}
            </div>

            <div className="domino-player-actions">
                {renderPlayerTiles()}
                {renderActionButtons()}
            </div>

            {popupMessage && (
                <div className="domino-popup-message">
                    {popupMessage}
                </div>
            )}

            <div className="domino-player-wrapper">
                {renderDominoPlayerArea(game, user)}
            </div>

            {renderGameEndPopup()}
            {renderLeaveGamePopup()}
            {renderPopup()}
        </div>
    );
};

export default DominoGame;